import React, { useState, useRef, useEffect, MutableRefObject } from 'react';
import styled from '@emotion/styled';
import { rem, rgba } from 'polished';
import { colors, mixins, spaces } from '../../core/styles/theme/default';
import { Link, Button } from '../../primitives';
import Container from '../..//helpers/Container';
import CookieOption from './CookieOption';
import { getBrowserCookie, setBrowserCookie } from './CookieHelpers';

const CookieWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100dvh;
  z-index: 999;
  background: ${rgba(colors.nightBlue700, 0.5)};
`;

const CookieBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  max-height: 100%;
  background: ${colors.white};
  padding: ${rem(spaces.delta)} 0;
  overflow-y: auto;

  ${mixins.MQ('medium')} {
    padding: ${rem(spaces.zeta)} 0;
  }
`;

const CookieContent = styled.div`
  margin-bottom: ${rem(spaces.delta)};

  ${mixins.MQ('medium')} {
    margin-bottom: ${rem(spaces.zeta)};
  }
`;

const StyledParagraph = styled.p`
  font-size: ${rem(16)};
  line-height: 1.75;
  color: ${colors.nightBlue700};
  margin: 0;

  ${mixins.MQ('medium')} {
    font-size: ${rem(18)};
  }

  ${({ theme }) => theme?.stylesExtension?.cookieBanner?.paragraph}
`;

const BlockTitle = styled.p`
  font-size: ${rem(20)};
  line-height: 1.75;
  color: ${colors.nightBlue700};
  font-weight: 500;
  margin: 0;

  ${mixins.MQ('medium')} {
    font-size: ${rem(24)};
  }

  ${({ theme }) => theme?.stylesExtension?.cookieBanner?.blockTitle}
`;

const CookieNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};

  ${mixins.MQ('wide')} {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }
`;

const CookieLinksWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;

  ${mixins.MQ('medium')} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(spaces.gamma)};
  }
`;

const CookieButtonsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: ${rem(spaces.gamma)};

  ${mixins.MQ('medium')} {
    flex-direction: row;
    align-items: center;
  }
`;

const StyledLink = styled(Link)`
  font-size: ${rem(16)};
  line-height: 1.75;
  color: ${colors.nightBlue700};
  position: relative;
  font-weight: 400;
  padding: ${rem(8)};
  margin-left: ${rem(-8)};

  ${mixins.MQ('medium')} {
    font-size: ${rem(18)};
  }

  ${mixins.MQ('wide')} {
    margin-bottom: ${rem(-10)};
  }

  ${({ theme }) => theme?.stylesExtension?.cookieBanner?.link}
`;

const waitForMtm = (callback: () => void) => {
  // Wait for _mtm to be available
  const checkMtm = () => {
    if (window._mtm) {
      callback();
    } else {
      setTimeout(checkMtm, 100);
    }
  };

  checkMtm();
};

const CookieBanner = ({ labels, links }: CookieBannerProps) => {
  const cookieName = 'ech_main';
  const [cookieString, setCookieString] = useState(
    getBrowserCookie(cookieName),
  );
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const analyticsCheckbox = useRef() as MutableRefObject<HTMLInputElement>;
  const marketingCheckbox = useRef() as MutableRefObject<HTMLInputElement>;

  const setCookie = (analytics = 0, marketing = 0) => {
    const cookieString = JSON.stringify({
      functional: 1,
      analytics,
      marketing,
    });

    if (analytics || marketing) {
      window._mtm.push({ event: 'consentAccepted' });
    }

    setBrowserCookie(cookieName, cookieString);
    setCookieString(cookieString);
  };

  useEffect(() => {
    waitForMtm(() => {
      if (cookieString) {
        const parsedCookie = JSON.parse(cookieString);

        window._mtm.push({
          event: 'mtm.consent_status',
          consent_functional: true,
          consent_analytics: !!parsedCookie.analytics,
          consent_marketing: !!parsedCookie.marketing,
        });
      } else {
        window._mtm.push({
          event: 'mtm.consent_status',
          consent_functional: false,
          consent_analytics: false,
          consent_marketing: false,
        });
      }
    });
  }, [cookieString]);

  return !cookieString ? (
    <CookieWrapper>
      <CookieBar role="banner" aria-label="Cookie Banner">
        <Container>
          <CookieContent>
            {!isSettingsOpen ? (
              <>
                <BlockTitle>{labels.mainTitle}.</BlockTitle>
                <StyledParagraph>{labels.mainText}</StyledParagraph>
              </>
            ) : (
              <>
                <BlockTitle>{labels.settingsTitle}</BlockTitle>
                <StyledParagraph>{labels.settingsText}</StyledParagraph>
                <div>
                  <CookieOption name="functional-cookie" checked disabled>
                    <span>{labels.functionalTitle}</span>{' '}
                    {labels.functionalText}
                  </CookieOption>
                  <CookieOption
                    name="analytics-cookie"
                    refItem={analyticsCheckbox}
                  >
                    <span>{labels.analyticsTitle}</span> {labels.analyticsText}
                  </CookieOption>
                  <CookieOption
                    name="marketing-cookie"
                    refItem={marketingCheckbox}
                  >
                    <span>{labels.marketingTitle}</span> {labels.marketingText}
                  </CookieOption>
                </div>
              </>
            )}
          </CookieContent>
          <CookieNav>
            <CookieLinksWrapper>
              <StyledLink
                linkText={links.linkOne.title}
                link={{
                  href: links.linkOne.href,
                  title: links.linkOne.title,
                }}
              />
              <StyledLink
                linkText={links.linkTwo.title}
                link={{
                  href: links.linkTwo.href,
                  title: links.linkTwo.title,
                }}
              />
            </CookieLinksWrapper>
            <CookieButtonsWrapper>
              {!isSettingsOpen ? (
                <>
                  <Button
                    buttonText={labels.openSettings}
                    onClick={() => setIsSettingsOpen(true)}
                    outlined
                  />
                  <Button
                    buttonText={labels.acceptAll}
                    onClick={() => setCookie(1, 1)}
                  />
                </>
              ) : (
                <Button
                  buttonText={labels.saveSettings}
                  onClick={() =>
                    setCookie(
                      +analyticsCheckbox.current.checked,
                      +marketingCheckbox.current.checked,
                    )
                  }
                />
              )}
            </CookieButtonsWrapper>
          </CookieNav>
        </Container>
      </CookieBar>
    </CookieWrapper>
  ) : null;
};

export type CookieBannerProps = {
  labels: {
    mainTitle: string;
    mainText: string;
    settingsTitle: string;
    settingsText: string;
    openSettings: string;
    saveSettings: string;
    acceptAll: string;
    functionalTitle: string;
    functionalText: string;
    analyticsTitle: string;
    analyticsText: string;
    marketingTitle: string;
    marketingText: string;
  };
  links: {
    linkOne: {
      href: string;
      title: string;
    };
    linkTwo: {
      href: string;
      title: string;
    };
  };
};

export default CookieBanner;
